import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    Send, Sparkles, Bot, User, ArrowRight, Loader2, FileText, Download,
    Mail, CheckCircle2, Zap, Building2, MessageSquare, Wand2,
} from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { apiPost, getAccessToken } from "@/lib/api";
import pt from "@/lib/i18n";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const CATEGORY_COLOR = {
    sdwan: "#C87941", mpls: "#2D6A8A", firewall: "#7A5A9C",
    cloud_connect: "#5C9EAD", voip: "#E8A96A", iot: "#2D7A4F",
    dedicated_internet: "#A0612E", managed_wifi: "#94A3B8",
};

const TypingDots = () => (
    <div className="flex gap-1 items-center px-3 py-2">
        {[0, 1, 2].map((i) => (
            <motion.span
                key={i}
                className="w-1.5 h-1.5 rounded-full bg-[color:var(--copper)]"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.15 }}
            />
        ))}
    </div>
);

const CopilotPage = () => {
    const navigate = useNavigate();
    const [company, setCompany] = useState("");
    const [problem, setProblem] = useState("");
    const [session, setSession] = useState(null); // {id, opening, ...}
    const [messages, setMessages] = useState([]); // [{role:user|assistant|system, content, ts}]
    const [composing, setComposing] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [currentResponse, setCurrentResponse] = useState("");
    const [recommendations, setRecommendations] = useState([]);
    const [readyForProposal, setReadyForProposal] = useState(false);
    const [proposalLoading, setProposalLoading] = useState(false);
    const [proposalResult, setProposalResult] = useState(null);
    const [systemMsg, setSystemMsg] = useState("");
    const messagesEndRef = useRef(null);
    const eventSourceRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, currentResponse]);

    const start = async () => {
        if (!problem.trim()) return;
        setSystemMsg(pt.copilot.analyzingNeeds);
        try {
            const r = await apiPost("/copilot/start", {
                customer_problem_statement: problem,
                company_name: company || undefined,
            });
            setSession(r);
            setMessages([{ role: "assistant", content: r.opening_message }]);
            setSystemMsg("");
        } catch (e) {
            setSystemMsg("");
        }
    };

    const sendMessage = () => {
        if (!composing.trim() || !session) return;
        const userMsg = composing.trim();
        setComposing("");
        setMessages((prev) => [...prev, { role: "user", content: userMsg }]);
        setStreaming(true);
        setCurrentResponse("");
        const tokenStr = getAccessToken();
        const url = new URL(`${API_BASE}/copilot/stream`);
        url.searchParams.set("session_id", session.session_id);
        url.searchParams.set("message", userMsg);
        url.searchParams.set("token", tokenStr || "");
        const es = new EventSource(url.toString());
        eventSourceRef.current = es;

        let acc = "";
        es.addEventListener("token", (ev) => {
            try {
                const data = JSON.parse(ev.data);
                acc += data.text;
                setCurrentResponse(acc);
            } catch (e) {}
        });
        es.addEventListener("recommendation", (ev) => {
            try {
                const data = JSON.parse(ev.data);
                setRecommendations(data.products || []);
                setReadyForProposal(!!data.ready_for_proposal);
                if (data.products?.length) {
                    setSystemMsg(`${pt.copilot.productsIdentified}: ${data.products.map(p => p.name).join(", ")}`);
                }
                if (data.ready_for_proposal) {
                    setSystemMsg(pt.copilot.readyForProposal);
                }
            } catch (e) {}
        });
        es.addEventListener("done", (ev) => {
            try {
                const data = JSON.parse(ev.data);
                setMessages((prev) => [...prev, { role: "assistant", content: data.text || acc }]);
                setCurrentResponse("");
                setStreaming(false);
                es.close();
            } catch (e) {
                setStreaming(false);
                es.close();
            }
        });
        es.onerror = () => {
            setStreaming(false);
            es.close();
        };
    };

    const generateProposal = async () => {
        if (!session) return;
        setProposalLoading(true);
        setSystemMsg(pt.copilot.generatingProposal);
        try {
            const r = await apiPost(`/copilot/${session.session_id}/generate-proposal`);
            setProposalResult(r);
            setSystemMsg(pt.copilot.proposalReady);
        } catch (e) {
            setSystemMsg("Erro ao gerar proposta.");
        }
        setProposalLoading(false);
    };

    return (
        <div data-testid="copilot-page" className="space-y-4">
            <PageHeader title={pt.copilot.title} subtitle={pt.copilot.subtitle} testId="copilot-header" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: "75vh" }}>
                {/* LEFT — Deal context */}
                <div className="lg:col-span-3">
                    <GlassCard padding="p-5" className="h-full">
                        <div className="flex items-center gap-2 mb-4">
                            <Building2 size={16} className="text-[color:var(--copper)]" />
                            <h3 className="font-display text-base text-[color:var(--slate-deep)]">{pt.copilot.contextTitle}</h3>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.16em] text-[color:var(--slate-mid)] font-mono mb-1.5">
                                    {pt.copilot.companyLabel}
                                </label>
                                <GlassInput
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                    placeholder={pt.salesExtra?.companySample || "BFA Banco de Fomento"}
                                    disabled={!!session}
                                    data-testid="copilot-company-input"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.16em] text-[color:var(--slate-mid)] font-mono mb-1.5">
                                    {pt.copilot.problemLabel}
                                </label>
                                <textarea
                                    rows={6}
                                    value={problem}
                                    onChange={(e) => setProblem(e.target.value)}
                                    placeholder={pt.copilot.problemPlaceholder}
                                    disabled={!!session}
                                    className="w-full px-3 py-2 rounded-lg bg-white/80 border border-[color:var(--glass-border)] font-body text-sm text-[color:var(--slate-deep)] focus:outline-none focus:border-[color:var(--copper)] disabled:opacity-60 resize-none"
                                    data-testid="copilot-problem-input"
                                />
                            </div>
                            {!session ? (
                                <NxButton
                                    variant="copper"
                                    onClick={start}
                                    disabled={!problem.trim()}
                                    className="w-full"
                                    icon={<Sparkles size={14} />}
                                    data-testid="btn-start-copilot"
                                >
                                    {pt.copilot.startBtn}
                                </NxButton>
                            ) : (
                                <div className="text-center">
                                    <NxBadge variant="copper">{pt.common.sessionActive}</NxBadge>
                                    <p className="font-mono text-[10px] text-[color:var(--slate-mid)] mt-2 truncate">
                                        {session.session_id?.slice(0, 13)}…
                                    </p>
                                </div>
                            )}
                            {systemMsg && (
                                <motion.div
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="text-xs italic text-[color:var(--copper-dark)] flex items-center gap-1.5 mt-2"
                                    data-testid="copilot-system-msg"
                                >
                                    <Zap size={11} />
                                    {systemMsg}
                                </motion.div>
                            )}
                        </div>
                    </GlassCard>
                </div>

                {/* CENTRE — Chat */}
                <div className="lg:col-span-6">
                    <GlassCard padding="p-0" className="h-full flex flex-col overflow-hidden">
                        <div className="p-3 border-b border-[color:var(--glass-border)] flex items-center gap-2">
                            <MessageSquare size={14} className="text-[color:var(--copper)]" />
                            <p className="font-body text-sm text-[color:var(--slate-deep)] font-medium">{pt.copilot.aiCopilot}</p>
                            {streaming && <TypingDots />}
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "linear-gradient(180deg, rgba(248,243,235,0.4), rgba(248,243,235,0))" }} data-testid="copilot-messages">
                            {!session ? (
                                <div className="h-full flex items-center justify-center text-center">
                                    <div>
                                        <Sparkles size={32} className="mx-auto mb-2 text-[color:var(--copper)]" />
                                        <p className="font-body text-sm text-[color:var(--slate-mid)] max-w-xs mx-auto">
                                            Descreva o problema do cliente à esquerda para iniciar uma sessão com o Co-Piloto IA.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <AnimatePresence initial={false}>
                                    {messages.map((m, idx) => {
                                        const isAssistant = m.role === "assistant";
                                        return (
                                            <motion.div
                                                key={idx}
                                                initial={{ opacity: 0, y: 6 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className={"flex gap-2 " + (isAssistant ? "justify-end" : "justify-start")}
                                            >
                                                {!isAssistant && (
                                                    <div className="rounded-full p-1.5 self-end" style={{ background: "var(--bg-surface)", color: "var(--slate-mid)" }}>
                                                        <User size={14} />
                                                    </div>
                                                )}
                                                <div
                                                    className={"max-w-[78%] rounded-2xl px-4 py-2.5 font-body text-sm whitespace-pre-wrap " + (isAssistant
                                                        ? "bg-gradient-to-br from-[color:var(--copper-light)] to-[color:var(--copper)] text-white"
                                                        : "bg-white/90 text-[color:var(--slate-deep)] border border-[color:var(--glass-border)]")}
                                                >
                                                    <p className="font-mono text-[10px] opacity-80 mb-1 flex items-center gap-1">
                                                        {isAssistant ? <Bot size={10} /> : <User size={10} />}
                                                        {isAssistant ? pt.copilot.aiCopilot : pt.copilot.agent}
                                                    </p>
                                                    <p className="leading-relaxed">{m.content}</p>
                                                </div>
                                                {isAssistant && (
                                                    <div className="rounded-full p-1.5 self-end" style={{ background: "var(--copper-subtle)", color: "var(--copper)" }}>
                                                        <Bot size={14} />
                                                    </div>
                                                )}
                                            </motion.div>
                                        );
                                    })}
                                    {streaming && currentResponse && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="flex gap-2 justify-end"
                                        >
                                            <div className="max-w-[78%] rounded-2xl px-4 py-2.5 font-body text-sm whitespace-pre-wrap bg-gradient-to-br from-[color:var(--copper-light)] to-[color:var(--copper)] text-white">
                                                <p className="font-mono text-[10px] opacity-80 mb-1 flex items-center gap-1">
                                                    <Bot size={10} /> {pt.copilot.aiCopilot}
                                                </p>
                                                <p className="leading-relaxed">{currentResponse}<span className="inline-block w-1 h-3 bg-white/80 ml-0.5 animate-pulse" /></p>
                                            </div>
                                            <div className="rounded-full p-1.5 self-end" style={{ background: "var(--copper-subtle)", color: "var(--copper)" }}>
                                                <Bot size={14} />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                        {session && (
                            <div className="p-3 border-t border-[color:var(--glass-border)] bg-[color:var(--bg-surface)]">
                                <div className="flex gap-2">
                                    <textarea
                                        rows={2}
                                        value={composing}
                                        onChange={(e) => setComposing(e.target.value)}
                                        placeholder={pt.copilot.chatPlaceholder}
                                        className="flex-1 px-3 py-2 rounded-lg bg-white/80 border border-[color:var(--glass-border)] font-body text-sm focus:outline-none focus:border-[color:var(--copper)] resize-none"
                                        data-testid="copilot-composer"
                                        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendMessage(); }}
                                        disabled={streaming}
                                    />
                                    <NxButton onClick={sendMessage} disabled={!composing.trim() || streaming} icon={<Send size={14} />} data-testid="btn-send-copilot" variant="copper">
                                        {pt.copilot.send}
                                    </NxButton>
                                </div>
                            </div>
                        )}
                    </GlassCard>
                </div>

                {/* RIGHT — Recommendations */}
                <div className="lg:col-span-3">
                    <GlassCard padding="p-5" className="h-full flex flex-col">
                        <div className="flex items-center gap-2 mb-3">
                            <Wand2 size={16} className="text-[color:var(--copper)]" />
                            <h3 className="font-display text-base text-[color:var(--slate-deep)]">{pt.copilot.recommendationsTitle}</h3>
                        </div>
                        <div className="flex-1 overflow-y-auto space-y-2" data-testid="copilot-recommendations">
                            {recommendations.length === 0 ? (
                                <p className="font-body text-xs text-[color:var(--slate-mid)] italic mt-4">
                                    {pt.copilot.recommendationsEmpty}
                                </p>
                            ) : (
                                <AnimatePresence>
                                    {recommendations.map((p, i) => (
                                        <motion.div
                                            key={p.product_id + i}
                                            initial={{ opacity: 0, scale: 0.92, y: 10 }}
                                            animate={{ opacity: 1, scale: 1, y: 0 }}
                                            transition={{ delay: i * 0.08 }}
                                            className="p-3 rounded-lg border bg-white/80"
                                            style={{ borderColor: `${CATEGORY_COLOR[p.category]}66` }}
                                            data-testid={`reco-card-${p.product_id}`}
                                        >
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wide" style={{ background: `${CATEGORY_COLOR[p.category]}22`, color: CATEGORY_COLOR[p.category] }}>
                                                    {p.category}
                                                </span>
                                                <span className="font-mono text-[10px] text-[color:var(--copper-dark)]">
                                                    USD {p.base_price_usd}
                                                </span>
                                            </div>
                                            <p className="font-display text-sm text-[color:var(--slate-deep)] leading-tight mb-1">{p.name}</p>
                                            <p className="font-body text-xs text-[color:var(--slate-mid)] leading-snug italic">
                                                {p.rationale}
                                            </p>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            )}
                        </div>
                        {recommendations.length > 0 && !proposalResult && (
                            <NxButton
                                variant="copper"
                                onClick={generateProposal}
                                disabled={proposalLoading}
                                className="w-full mt-4"
                                icon={proposalLoading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                                data-testid="btn-generate-proposal"
                            >
                                {proposalLoading ? pt.copilot.generatingProposal : pt.copilot.generateProposal}
                            </NxButton>
                        )}
                        {proposalResult && (
                            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 p-3 rounded-lg bg-[color:var(--copper-subtle)] border border-[color:var(--copper)]" data-testid="proposal-result">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle2 size={14} className="text-[color:var(--success)]" />
                                    <p className="font-mono text-xs text-[color:var(--copper-dark)] font-medium">{proposalResult.proposal_number}</p>
                                </div>
                                <p className="font-mono text-lg text-[color:var(--copper-dark)]">USD {Number(proposalResult.total_usd).toLocaleString("pt-PT")}</p>
                                <p className="font-mono text-[10px] text-[color:var(--slate-mid)] mb-2">AOA {Number(proposalResult.total_aoa).toLocaleString("pt-PT")}</p>
                                {proposalResult.pdf_url && (
                                    <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}${proposalResult.pdf_url}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[color:var(--copper)] text-white text-xs font-body hover:bg-[color:var(--copper-dark)] transition-colors w-full justify-center"
                                        data-testid="btn-download-pdf"
                                    >
                                        <Download size={12} />
                                        {pt.copilot.downloadPdf}
                                    </a>
                                )}
                            </motion.div>
                        )}
                    </GlassCard>
                </div>
            </div>
        </div>
    );
};

export default CopilotPage;
